/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Divider,
  Select,
  InputAdornment,
  MenuItem,
  TextField,
  IconButton
} from '@mui/material'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useTranslation } from 'react-i18next'
import { middlePanelStyles } from '../../styles/classes/FormBuilderClasses'
import {
  getFirstKeyFromObject,
  cloneDeep,
  elementKeys,
  elementTypes
} from '../../utils/formBuilder'

import TabPanel from './TabPanel'
import Sortable from './Sortable'
import closeIcon from '../../assets/icons/close.svg'
import DraggedElement from './DraggedElement'
import { statusOptions } from '../../utils/fieldForms'
import DroppableContainer from './DNDMultiple/DroppableContainer'
import { userHasAuthorization } from '../../services/AuthService'
import { faFile } from '@fortawesome/sharp-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import { loadingActions } from '../../store/loading'
import { store } from '../../store'

const MiddlePanel = ({
  changes,
  onSaveButtonClick,
  onPublishButtonClick,
  onDeactivateButtonClick,
  status,
  activeItem,
  missingData,
  mandatoryFields,
  selectedObjectCounter,
  setSelectedItem,
  setChanges,
  onShowPreview,
  onSelectItem,
  sectionsList,
  onEditSectionName,
  pageIndex,
  emptySections,
  activeId,
  bottomRef,
  recentlyAddedId
}) => {
  const loadingAuditTrail = useSelector(
    state => state.loading.loadingAuditTrail
  )
  const panelsQuery = useSelector(state => state.loading.panelsQuery)
  const sectionsIds = sectionsList?.map(section =>
    getFirstKeyFromObject(section)
  )
  const [activeSections, setActiveSections] = useState(
    Array(sectionsIds?.length).fill(true)
  )
  const { t } = useTranslation()
  const { classes } = middlePanelStyles()
  const [centerTabValue, setCenterTabValue] = useState(0)
  const rightButtonAction =
    status === statusOptions.PUBLISHED
      ? onDeactivateButtonClick
      : onPublishButtonClick
  const rightButtonLabel =
    status === statusOptions.PUBLISHED
      ? t('form_builder.center_panel.deactivate')
      : t('form_builder.center_panel.publish')
  const modeTitle =
    status === statusOptions.PUBLISHED
      ? `**${t('form_builder.center_panel.published_mode')}**`
      : `**${t('form_builder.center_panel.draft_mode')}**`
  const publishedMode = status === statusOptions.PUBLISHED
  const handleCenterTabChange = (event, newValue) => {
    setCenterTabValue(newValue)
  }

  useEffect(() => {
    setActiveSections(Array(sectionsList?.length).fill(true))
  }, sectionsList)

  const showSectionHandler = sectionIdx => {
    setActiveSections(prevValues => {
      const modValues = cloneDeep(prevValues)
      modValues[sectionIdx] = !modValues[sectionIdx]
      return modValues
    })
  }
  // filter by query
  let filteredData = cloneDeep(sectionsList)
  if (panelsQuery?.length > 0) {
    const lowerCaseQuery = panelsQuery.toLowerCase()
    const tmp = []
    filteredData.forEach(section => {
      const sectionKey = getFirstKeyFromObject(section)
      section[sectionKey].questions = section[sectionKey].questions.filter(
        item => {
          // search inside type translated
          const keyTextTranslated = t(
            `form_builder.elements.${item[getFirstKeyFromObject(item)].type}`
          )
          const elementTitle = item[getFirstKeyFromObject(item)].title
          return (
            keyTextTranslated.toLowerCase().includes(lowerCaseQuery) ||
            elementTitle.toLowerCase().includes(lowerCaseQuery)
          )
        }
      )
      if (section[sectionKey].questions.length > 0) {
        tmp.push(section)
      }
    })
    filteredData = tmp
  }

  const handleChangeSearch = event => {
    const value = event.target.value
    store.dispatch(loadingActions.panelsQuery(value))
  }

  const handleClearSearch = () => {
    store.dispatch(loadingActions.panelsQuery(''))
  }

  const changeTitleHandler = titleValue => {
    if (activeItem) {
      if (!changes) setChanges(true)
      setSelectedItem(prevItemValue => {
        const newObject = cloneDeep(prevItemValue)
        const selectedItemKey = getFirstKeyFromObject(newObject)
        newObject[selectedItemKey][elementKeys.TITLE] = titleValue
        return newObject
      })
    }
  }

  const PLACEHOLDER_ID = 'placeholder'

  const emptyComponent = () => {
    return (
      <Box className={classes.emptyComponentContainer}>
        <Typography className={classes.emptyMessageTitle}>
          {t('form_builder.center_panel.lets_build')}
        </Typography>
        <Typography className={classes.emptyMessage}>
          {t('form_builder.center_panel.empty_message')}
        </Typography>
      </Box>
    )
  }

  const emptySeachComponent = () => {
    return (
      <Box className={classes.emptySearchContainer}>
        <Typography className={classes.emptySeachMessage}>
          {t('form_builder.center_panel.empty_search_message')}
        </Typography>
      </Box>
    )
  }

  const pagesAndSearch = () => {
    return (
      <Box
        display={'flex'}
        flexDirection="row"
        className={classes.secondaryBarContainer}
      >
        <Select
          startAdornment={
            <InputAdornment position="start">
              <FontAwesomeIcon icon={faFile} className={classes.fileIcon} />
            </InputAdornment>
          }
          labelId="label"
          id="select"
          value="1"
          disabled
          className={classes.selector}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <IconButton>
                  <img src={closeIcon} alt="lock icon" />
                </IconButton>
              </InputAdornment>
            )
          }}
        >
          <MenuItem value="1">Page 1</MenuItem>
        </Select>
        <TextField
          placeholder="Search"
          onChange={handleChangeSearch}
          value={panelsQuery}
          id="input-with-icon-textfield"
          InputProps={{
            className: classes.searchBar,
            endAdornment: panelsQuery?.length > 0 && (
              <InputAdornment position="start" className={classes.lockIcon}>
                <IconButton onClick={handleClearSearch}>
                  <img src={closeIcon} alt="lock icon" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>
    )
  }

  const sortableQuestion = (item, index, questionsLength) => {
    const itemId = getFirstKeyFromObject(item)
    const activeItemId = getFirstKeyFromObject(activeItem)
    let selectedTimes = 0
    if (selectedObjectCounter[itemId]) {
      selectedTimes = selectedObjectCounter[itemId]
    }
    let recentlyAdded = false
    if (recentlyAddedId && recentlyAddedId === itemId) {
      recentlyAdded = true
    }
    const disableEdit =
      publishedMode ||
      item[itemId].type === elementTypes.MATERIAL_PARTS ||
      item[itemId].type === elementTypes.AUTHORIZATION_FIELD
    return (
      <Sortable key={itemId} id={itemId} disabled={publishedMode}>
        <DraggedElement
          recentlyAdded={recentlyAdded}
          disableEdit={disableEdit}
          onSelectItem={onSelectItem}
          item={item}
          selected={activeItemId === itemId}
          alreadySelected={selectedTimes > 0}
          mandatoryFields={mandatoryFields}
          onChangeTitle={changeTitleHandler}
        />
        <div
          className={`${
            questionsLength - 1 !== index && classes.elementsSeparation
          }`}
        />
      </Sortable>
    )
  }

  return (
    <Box className={classes.middlePanelContainer}>
      {/* tabs and buttons */}
      <Box className={classes.tabsContainer}>
        <Box flex={1}>
          <Tabs
            value={centerTabValue}
            onChange={handleCenterTabChange}
            indicatorColor="primary"
            textColor="inherit"
            TabIndicatorProps={{
              className: classes.tabIndicator
            }}
          >
            <Tab
              disableRipple
              label="Form Builder"
              className={classes.tabText}
            />
          </Tabs>
        </Box>
        <Box flex={1}>
          <Typography className={classes.modeTitle}>{modeTitle}</Typography>
        </Box>
        <Box flex={1}>
          <Box display="flex">
            <Button
              disabled={loadingAuditTrail}
              variant="outlined"
              className={classes.previewButton}
              onClick={onShowPreview}
            >
              {t('form_builder.preview')}
            </Button>
            <div style={{ width: '10px' }} />
            <Button
              disabled={!changes || missingData || loadingAuditTrail}
              onClick={onSaveButtonClick}
              className={classes.saveButton}
            >
              {t('form_builder.save')}
            </Button>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ margin: '10px' }}
            />
            <Button
              disabled={
                ((emptySections || changes) && !publishedMode) ||
                (publishedMode &&
                  !userHasAuthorization('published_field_forms:write')) ||
                loadingAuditTrail
              }
              onClick={rightButtonAction}
              variant="outlined"
              className={classes.rightButton}
            >
              {rightButtonLabel}
            </Button>
          </Box>
        </Box>
      </Box>
      {/* pages and search */}
      {pagesAndSearch()}

      <TabPanel value={centerTabValue} index={0}>
        <div className={classes.sectionsContainer}>
          <SortableContext
            items={[...sectionsIds, PLACEHOLDER_ID]}
            strategy={verticalListSortingStrategy}
          >
            {filteredData.length === 0 &&
              panelsQuery.length > 0 &&
              emptySeachComponent()}
            {filteredData.map((section, sectionIndex) => {
              const sectionKey = getFirstKeyFromObject(section)
              const sectionData = section[sectionKey]
              const sectionTitle = sectionData.title
              const questions = sectionData.questions
              const questionsIds = questions.map(question =>
                getFirstKeyFromObject(question)
              )
              return (
                <DroppableContainer
                  activeId={activeId}
                  questions={questions}
                  disabled={publishedMode}
                  key={sectionKey}
                  id={sectionKey}
                  label={sectionTitle}
                  columns={filteredData?.length}
                  items={filteredData}
                  sectionIndex={sectionIndex}
                  activeSections={activeSections}
                  onShowHideSection={showSectionHandler}
                  onEditSectionName={onEditSectionName}
                  pageIndex={pageIndex}
                >
                  <SortableContext
                    items={[...questionsIds, PLACEHOLDER_ID]}
                    strategy={verticalListSortingStrategy}
                  >
                    {questions?.length > 0 &&
                      questions.map((question, index) => {
                        return sortableQuestion(
                          question,
                          index,
                          questions?.length
                        )
                      })}
                    {questions?.length === 0 && emptyComponent()}
                  </SortableContext>
                </DroppableContainer>
              )
            })}
          </SortableContext>
          {<div ref={bottomRef} className={classes.bottomComponent} />}
        </div>
      </TabPanel>
    </Box>
  )
}

export default MiddlePanel
